import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import {
  ServerCog,
  Database,
  HardDrive,
  Mail,
  Activity,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { Button } from "../../components/ui/button";

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

export default function PlatformSystemHealth() {
  const { t } = useTranslation();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/health-stats");
      setHealth(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (loading || !health) {
    return <div className="h-32 animate-pulse rounded-xl bg-zinc-200/60" />;
  }

  const outboxTotal =
    health.outbox.pending + health.outbox.sent + health.outbox.failed;
  const failureRate = outboxTotal > 0 ? (health.outbox.failed / outboxTotal) * 100 : 0;
  const healthy = failureRate < 1;

  return (
    <div className="space-y-5" data-testid="platform-system">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">Serpix Admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            {t("platform.nav.system")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t("platform.system.subtitle")}
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          data-testid="platform-system-refresh"
          className="gap-1.5"
        >
          <RefreshCw className="h-4 w-4" /> {t("common.refresh")}
        </Button>
      </header>

      {/* Status banner */}
      <div
        className={[
          "flex items-center gap-3 rounded-xl border p-4",
          healthy
            ? "border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50"
            : "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50",
        ].join(" ")}
        data-testid="platform-system-status"
      >
        <div
          className={[
            "flex h-10 w-10 items-center justify-center rounded-full text-white shadow-md",
            healthy ? "bg-emerald-500" : "bg-amber-500",
          ].join(" ")}
        >
          {healthy ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
        </div>
        <div className="flex-1">
          <div className="font-semibold text-zinc-900">
            {healthy ? t("platform.system.all_ok") : t("platform.system.attention")}
          </div>
          <div className="text-xs text-zinc-600">
            {t("platform.system.server_time")}: {new Date(health.server_time).toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Failure rate</div>
          <div className={`font-display text-xl font-bold ${healthy ? "text-emerald-700" : "text-amber-700"}`}>
            {failureRate.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Storage / DB */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          icon={Database}
          label={t("platform.system.db_size")}
          value={formatBytes(health.db.dataSize || 0)}
          hint={`${(health.db.objects || 0).toLocaleString()} objects`}
          testId="platform-system-db-size"
          gradient="from-blue-500 to-indigo-600"
        />
        <StatCard
          icon={HardDrive}
          label={t("platform.system.index_size")}
          value={formatBytes(health.db.indexSize || 0)}
          hint={`${health.db.collections || 0} collections`}
          testId="platform-system-index-size"
          gradient="from-fuchsia-500 to-pink-500"
        />
        <StatCard
          icon={HardDrive}
          label={t("platform.system.storage")}
          value={formatBytes(health.attachments_bytes || 0)}
          hint={t("platform.system.attachments_hint")}
          testId="platform-system-attachments"
          gradient="from-amber-500 to-orange-500"
        />
      </div>

      {/* Outbox status */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="platform-system-outbox">
        <div className="mb-3 flex items-center gap-2">
          <Mail className="h-4 w-4 text-zinc-500" />
          <h3 className="font-semibold text-zinc-900">{t("platform.system.email_outbox")}</h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <OutboxCell
            label="Pending"
            value={health.outbox.pending}
            icon={Clock}
            tone="text-amber-700 bg-amber-50"
            testId="platform-system-outbox-pending"
          />
          <OutboxCell
            label="Sent"
            value={health.outbox.sent}
            icon={CheckCircle2}
            tone="text-emerald-700 bg-emerald-50"
            testId="platform-system-outbox-sent"
          />
          <OutboxCell
            label="Failed"
            value={health.outbox.failed}
            icon={XCircle}
            tone="text-red-700 bg-red-50"
            testId="platform-system-outbox-failed"
          />
        </div>
      </div>

      {/* Collections */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="platform-system-collections">
        <div className="mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-zinc-500" />
          <h3 className="font-semibold text-zinc-900">{t("platform.system.collections")}</h3>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {Object.entries(health.collections).map(([name, count]) => (
            <div
              key={name}
              className="rounded-lg border border-zinc-100 bg-zinc-50/60 px-3 py-2"
              data-testid={`platform-system-col-${name}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                {name}
              </div>
              <div className="font-display text-lg font-semibold text-zinc-900">
                {count?.toLocaleString?.() ?? "—"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, testId, gradient }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-5" data-testid={testId}>
      <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${gradient} opacity-10 blur-2xl`} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-500">{label}</div>
          <div className="mt-2 font-display text-2xl font-bold text-zinc-950">{value}</div>
          <div className="mt-1 text-[11px] text-zinc-500">{hint}</div>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} text-white shadow-md`}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

function OutboxCell({ label, value, icon: Icon, tone, testId }) {
  return (
    <div className={`rounded-lg px-4 py-3 ${tone}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 font-display text-2xl font-bold">{value.toLocaleString()}</div>
    </div>
  );
}
